import { useQueryClient } from '@tanstack/react-query';

import { AppShell } from '../components/shell/AppShell';
import { AuthPage } from '../features/auth/AuthPage';
import { useCurrentUser, userKeys } from '../features/users/users.api';
import { I18nProvider } from '../i18n/I18nProvider';
import { useI18n } from '../i18n/useI18n';
import { ApiError } from '../lib/api-client';
import type { CurrentUser } from '../lib/api-types';
import { QueryProvider } from './QueryProvider';
import { ThemeProvider } from './ThemeProvider';

export function App() {
  return (
    <QueryProvider>
      <I18nProvider>
        <ThemeProvider>
          <AuthGate />
        </ThemeProvider>
      </I18nProvider>
    </QueryProvider>
  );
}

function AuthGate() {
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  const setAuthenticated = (user: CurrentUser) => {
    queryClient.setQueryData(userKeys.current, user);
  };

  if (currentUser.isPending) {
    return (
      <main aria-busy="true" aria-label={t('auth.loading')} className="auth-page" id="main-content">
        <div className="auth-loading" />
      </main>
    );
  }

  if (currentUser.data !== undefined) return <AppShell />;

  if (currentUser.error instanceof ApiError && currentUser.error.status === 401) {
    return <AuthPage onAuthenticated={setAuthenticated} />;
  }

  if (currentUser.error !== null) {
    return (
      <main className="auth-page" id="main-content">
        <section className="auth-panel auth-panel-compact" role="alert">
          <div className="auth-brand">
            <span>{t('auth.brand')}</span>
          </div>
          <div className="auth-heading">
            <h1>{t('dashboard.loadError')}</h1>
            <span>{t('dashboard.loadErrorDescription')}</span>
          </div>
          <button
            className="button button-primary"
            onClick={() => void currentUser.refetch()}
            type="button"
          >
            {t('auth.retry')}
          </button>
        </section>
      </main>
    );
  }

  return <AppShell />;
}
